import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';

const BASE_API_KEY = 'board/:boardId/reporting/charts';

const mapChart = (
	response: Api.IOpportunityReportingChartsResponse,
	key: string
): Api.IOpportunityReportingChartRow[] => {
	const chart = response.charts?.[key];
	return (
		chart?.data?.map(d => ({
			label: d.label,
			value: d.value,
			count: d.count,
		})) ?? []
	);
};

const transformChartData = (
	response: Api.IOpportunityReportingChartsResponse
): Api.IOpportunityReportingChartsData => ({
	byStage: mapChart(response, 'byStage'),
	byCloseDate: mapChart(response, 'byCloseDate'),
	byCreationDate: mapChart(response, 'byCreationDate'),
	byTag: mapChart(response, 'byTag'),
	dealSizeDistribution: mapChart(response, 'dealSizeDistribution'),
	byOwner: mapChart(response, 'byOwner'),
	topCompanies: mapChart(response, 'topCompanies'),
});

export function useOpportunityReportingChartsQuery({
	boardId,
	filterRequest,
	charts,
	enabled,
	refetchOnWindowFocus = false,
}: {
	boardId: string;
	filterRequest: Api.IOpportunityReportingFilterRequest;
	charts: Api.IOpportunityChartConfiguration[];
	enabled?: boolean;
	refetchOnWindowFocus?: boolean;
}) {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		meta: {
			toastErrorMessage: 'Failed to load opportunity reporting charts',
		},
		queryFn: async (): Promise<Api.IOpportunityReportingChartsData> => {
			const response = await userSession.webServiceHelper.callAsync<Api.IOpportunityReportingChartsResponse>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `board/${boardId}/reporting/charts`,
				}),
				'POST',
				{ filter: filterRequest, charts }
			);
			return transformChartData(response);
		},
		queryKey: [BASE_API_KEY, boardId, filterRequest, charts.map(c => c.key)],
		refetchOnWindowFocus,
	});
}
